import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeAnalyticsTrends.graphql';
import { Query, QueryyoutubeAnalyticsTrendsArgs } from '../../__generated__/globalTypes';

export const useYoutubeAnalyticsTrendsQuery = (options?: QueryHookOptions<Query, QueryyoutubeAnalyticsTrendsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeAnalyticsTrendsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeAnalyticsTrendsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeAnalyticsTrendsArgs>
): QueryResult<Query, QueryyoutubeAnalyticsTrendsArgs> & {
  getYoutubeAnalyticsTrends: LazyQueryExecFunction<Query, QueryyoutubeAnalyticsTrendsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeAnalyticsTrends, result] = useLazyQuery<Query, QueryyoutubeAnalyticsTrendsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeAnalyticsTrends, ...result };
};

export const useYoutubeAnalyticsTrendsPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeAnalyticsTrends = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeAnalyticsTrendsArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeAnalyticsTrendsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeAnalyticsTrends };
};
// Suspense Query Hooks
export const useYoutubeAnalyticsTrendsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeAnalyticsTrendsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeAnalyticsTrendsArgs>(QUERY, {
    ...options
  });

  return result;
};
